import { useState } from "react";
import { Upload, FileText, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ReturnCompletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string | null;
  packageName: string;
  onComplete: () => void;
}

export const ReturnCompletionDialog = ({
  open,
  onOpenChange,
  packageId,
  packageName,
  onComplete,
}: ReturnCompletionDialogProps) => {
  const [confirmationCode, setConfirmationCode] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setReceiptFile(file);
      if (file.type.startsWith("image/")) {
        setReceiptPreview(URL.createObjectURL(file));
      } else {
        setReceiptPreview(null);
      }
    }
  };

  const handleSubmit = async () => {
    if (!packageId) return;

    if (!confirmationCode.trim() && !receiptFile) {
      toast.error("Please provide a confirmation code or upload a receipt");
      return;
    }

    setLoading(true);
    try {
      let receiptUrl: string | null = null;

      // Upload receipt if provided
      if (receiptFile) {
        const fileExt = receiptFile.name.split(".").pop();
        const fileName = `${packageId}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("return-receipts")
          .upload(fileName, receiptFile);

        if (uploadError) throw uploadError;
        receiptUrl = fileName;
      }

      // Update package
      const updateData: Record<string, any> = {
        status: "returned",
        returned_at: new Date().toISOString(),
      };

      if (confirmationCode.trim()) {
        updateData.confirmation_code = confirmationCode.trim();
      }
      if (receiptUrl) {
        updateData.return_receipt_url = receiptUrl;
      }

      const { error } = await (supabase as any)
        .from("packages")
        .update(updateData)
        .eq("id", packageId);

      if (error) throw error;

      toast.success("Package marked as returned");
      
      // Reset form
      setConfirmationCode("");
      setReceiptFile(null);
      setReceiptPreview(null);
      
      onOpenChange(false);
      onComplete();
    } catch (error: any) {
      toast.error(error.message || "Failed to mark package as returned");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmationCode("");
    setReceiptFile(null);
    setReceiptPreview(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Mark Package as Returned</DialogTitle>
          <DialogDescription>
            Upload return confirmation for {packageName}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Confirmation Code */}
          <div className="space-y-2">
            <Label htmlFor="confirmationCode">Confirmation Code</Label>
            <Input
              id="confirmationCode"
              placeholder="Enter return confirmation code..."
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              The code provided by the return location
            </p>
          </div>

          {/* Receipt Upload */}
          <div className="space-y-2">
            <Label>Receipt / Proof of Return</Label>
            <div className="border-2 border-dashed border-input rounded-lg p-4 hover:border-primary/50 transition-colors">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {receiptFile ? (
                  <div className="flex items-center gap-3">
                    {receiptPreview ? (
                      <img 
                        src={receiptPreview} 
                        alt="Receipt preview" 
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <FileText className="w-10 h-10 text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{receiptFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(receiptFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        setReceiptFile(null);
                        setReceiptPreview(null);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Upload className="w-8 h-8" />
                    <p className="text-sm">Click to upload receipt image or PDF</p>
                    <p className="text-xs">Max 10MB</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Info */}
          <div className="p-3 rounded-lg bg-muted text-sm">
            <p className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span>
                This will mark the package as "Returned" and start a 45-day retention timer 
                before auto-archiving.
              </span>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || (!confirmationCode.trim() && !receiptFile)}
          >
            {loading ? "Saving..." : "Mark as Returned"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
